import React, { useCallback } from 'react';
import { Button, MainLayout, Triptych } from '@app/components';
import { DeliveryZoneList, DeliveryZoneDeleteModal, DeliveryZoneCreate, DeliveryZoneUpdate } from '@app/features/DeliveryZone';
import { useResourceScreenHelpers } from '@app/hooks';
import { deliveryZoneApi } from '@app/store';
import { IDeliveryZone } from '@app/common';
import { RouteProp, useFocusEffect, useRoute } from '@react-navigation/native';
import { RootStackParamList } from '@app/types';
import { SettingHeader } from '@app/features/SettingHeader';
import { CheckPermissions } from '@app/components/CheckPermissions';
import { useAppSelector } from '@app/store/hooks';
import { RootState } from '@app/store/store';
import { View, Text, StyleSheet } from 'react-native';
import { theme } from '@app/theme';

export const DeliveryZoneScreen = () => {
  const route = useRoute<RouteProp<RootStackParamList, 'DeliveryZoneScreen'>>();

  const [deleteOne, { isLoading }] = deliveryZoneApi.useDeleteOneDeliveryZonesMutation();

   const selectedShop = useAppSelector((state: RootState) => state.app.selectedShop);

  // Pour les zones de livraison, l'API ne dispose pas d'un endpoint /singleResult :
  // on se contente donc des données déjà chargées (liste) sans requête supplémentaire.
  const isFetching = false;

  const { triptych, modal, onDelete, onUpdate, mode, selectedItem, item: deliveryZone } = useResourceScreenHelpers<IDeliveryZone>();

  const openCard = (item: Partial<IDeliveryZone>) => {
    triptych.open({ mode: 'UPDATE', data: item });
  }

  useFocusEffect(
    useCallback(() => {
      if (Boolean(route.params?._id)) {
        openCard({ _id: route.params._id, name: "Chargement..." });
      }
    }, [route])
  );

  return (
    <MainLayout
      header={(
        <SettingHeader>
          {!selectedShop ? (
            <Text style={styles.headerInfo}>
              Sélectionnez d'abord un restaurant dans le menu latéral pour gérer les zones de livraison.
            </Text>
          ) : (
            <CheckPermissions permissions={['dashboard::delivery-zones.create']}>
              <Button
                onPress={() => {
                  triptych.open({ mode: 'CREATE' });
                }}
                title='Ajouter une zone de livraison' />
            </CheckPermissions>
          )}
        </SettingHeader>
      )}>

      {!selectedShop ? (
        <View style={styles.emptyWrapper}>
          <View style={styles.emptyCard}>
            <Text style={styles.emptyTitle}>
              Aucun restaurant sélectionné
            </Text>
            <Text style={styles.emptyDescription}>
              Choisissez un restaurant dans le menu latéral gauche pour continuer.
            </Text>
          </View>
        </View>
      ) : (
        <>
          <DeliveryZoneDeleteModal
            {...modal}
            onDelete={(deliveryZone) => onDelete(deleteOne(deliveryZone._id!))}
            isLoading={isLoading} />

          <Triptych
            list={(
              <DeliveryZoneList
                selectedRowItem={selectedItem}
                onRowPress={(item) => triptych.close({ data: item, mode: 'UPDATE' })}
                onUpdate={openCard}
                onDelete={modal.open} />
            )}
            form={(
              mode === 'CREATE'
                ? <DeliveryZoneCreate />
                : <DeliveryZoneUpdate
                  deliveryZone={deliveryZone ?? selectedItem}
                  toggle={(mode) => mode === 'UPDATE' ? openCard(selectedItem) : triptych.close()}
                  isFetching={isFetching}
                  onDelete={modal.open} />
            )} />
        </>
      )}
    </MainLayout>
  );
};

const styles = StyleSheet.create({
  emptyWrapper: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 40,
  },
  emptyCard: {
    maxWidth: 640,
    width: '100%',
    paddingVertical: 32,
    paddingHorizontal: 32,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 4,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colors.oxfordblue,
    textAlign: 'center',
    marginBottom: 12,
  },
  emptyDescription: {
    fontSize: 16,
    color: theme.colors.oxfordblue,
    opacity: 0.75,
    textAlign: 'center',
    lineHeight: 22,
  },
  headerInfo: {
    fontSize: 16,
    color: theme.colors.babypowder,
    opacity: 0.9,
  },
});

